import { useState, useMemo, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams, useNavigate } from "react-router";
import { getBooks } from "@/lib/supabase";
import { TISACard, BookCard } from "@/components/tisa/TISACard";
import { TISAInput } from "@/components/tisa/TISAInput";
import { TISAButton } from "@/components/tisa/TISAButton";
import {
  IB_PYP_THEMES,
  type ThemeColor,
  type BookType,
} from "@/types/database";
import {
  Search,
  Filter,
  BookOpen,
  Star,
  Clock,
  ArrowLeft,
  Home,
} from "lucide-react";
import { cn } from "@/lib/utils";

export function BookSearch() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTheme, setSelectedTheme] = useState<ThemeColor | "all">("all");
  const [selectedType, setSelectedType] = useState<BookType | "all">("all");
  const [availableOnly, setAvailableOnly] = useState(true);
  const [showFilters, setShowFilters] = useState(false);

  // Initialize state from URL parameters
  useEffect(() => {
    const theme = searchParams.get("theme") as ThemeColor;
    const search = searchParams.get("search");
    const type = searchParams.get("type") as BookType;

    if (theme && Object.keys(IB_PYP_THEMES).includes(theme)) {
      setSelectedTheme(theme);
    }
    if (search) {
      setSearchQuery(search);
    }
    if (type && ["FIC", "NF"].includes(type)) {
      setSelectedType(type);
    }
  }, [searchParams]);

  // Fetch all books
  const {
    data: books = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ["books"],
    queryFn: () => getBooks(),
  });

  // Filter books based on search criteria
  const filteredBooks = useMemo(() => {
    return books.filter((book) => {
      // Search query filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesTitle = book.title.toLowerCase().includes(query);
        const matchesAuthor = book.author.toLowerCase().includes(query);
        const matchesTags = book.tags?.some((tag) =>
          tag.toLowerCase().includes(query),
        );
        if (!matchesTitle && !matchesAuthor && !matchesTags) return false;
      }

      // Theme filter
      if (selectedTheme !== "all" && book.theme_color !== selectedTheme)
        return false;

      // Type filter
      if (selectedType !== "all" && book.type !== selectedType) return false;

      // Available only filter
      if (availableOnly) {
        const hasAvailableCopies = book.copies?.some(
          (copy) => copy.status === "available",
        );
        if (!hasAvailableCopies) return false;
      }

      return true;
    });
  }, [books, searchQuery, selectedTheme, selectedType, availableOnly]);

  // Group books by theme for rainbow display
  const booksByTheme = useMemo(() => {
    const grouped: Record<ThemeColor, typeof books> = {
      blue: [],
      green: [],
      yellow: [],
      red: [],
      purple: [],
      orange: [],
    };

    filteredBooks.forEach((book) => {
      grouped[book.theme_color].push(book);
    });

    return grouped;
  }, [filteredBooks]);

  const handleClearFilters = () => {
    setSearchQuery("");
    setSelectedTheme("all");
    setSelectedType("all");
    setAvailableOnly(true);
  };

  if (error) {
    return (
      <div className="space-y-6">
        <h1 className="text-child-3xl font-bold text-primary">
          Find Your Next Adventure 🔍
        </h1>
        <TISACard variant="outline" className="text-center p-8">
          <div className="text-child-lg text-destructive mb-4">
            📚 Oops! Something went wrong
          </div>
          <p className="text-muted-foreground">
            We couldn't load the books right now. Please try again!
          </p>
        </TISACard>
      </div>
    );
  }

  return (
    <div className="space-y-8 max-w-7xl mx-auto">
      {/* Navigation */}
      <div className="flex items-center justify-between">
        <TISAButton
          onClick={() => navigate("/student/dashboard")}
          variant="outline"
          icon={<Home className="h-4 w-4" />}
        >
          Dashboard
        </TISAButton>

        <TISAButton
          onClick={() => navigate(-1)}
          variant="outline"
          icon={<ArrowLeft className="h-4 w-4" />}
        >
          Back
        </TISAButton>
      </div>

      {/* Header */}
      <div className="text-center space-y-4">
        <h1 className="text-child-4xl font-bold text-primary">
          Find Your Next Adventure 🔍
        </h1>
        <p className="text-child-lg text-muted-foreground">
          Discover amazing books from our library collection!
        </p>
      </div>

      {/* Search and Filters */}
      <TISACard className="space-y-6">
        {/* Main Search */}
        <div className="relative">
          <TISAInput
            type="search"
            placeholder="Search for books, authors, or topics..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="lg"
            icon={<Search className="h-5 w-5" />}
            iconPosition="left"
            className="pr-20"
          />
          <TISAButton
            onClick={() => setShowFilters(!showFilters)}
            variant="outline"
            size="md"
            className="absolute right-2 top-2"
            icon={<Filter className="h-4 w-4" />}
          >
            Filters
          </TISAButton>
        </div>

        {/* Advanced Filters */}
        {showFilters && (
          <div className="space-y-6 p-6 bg-muted/30 rounded-xl">
            <div className="flex items-center justify-between">
              <h3 className="text-child-lg font-semibold">Filter Books</h3>
              <TISAButton
                onClick={handleClearFilters}
                variant="ghost"
                size="sm"
              >
                Clear All
              </TISAButton>
            </div>

            {/* IB Theme Filter */}
            <div className="space-y-3">
              <label className="text-child-base font-medium">
                IB PYP Themes
              </label>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
                <button
                  onClick={() => setSelectedTheme("all")}
                  className={cn(
                    "p-3 rounded-lg text-child-sm font-medium transition-all",
                    selectedTheme === "all"
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted hover:bg-muted/80",
                  )}
                >
                  All Themes
                </button>
                {Object.entries(IB_PYP_THEMES).map(([color, theme]) => (
                  <button
                    key={color}
                    onClick={() => setSelectedTheme(color as ThemeColor)}
                    className={cn(
                      `theme-${color} p-3 rounded-lg text-center transition-all`,
                      selectedTheme === color
                        ? "ring-2 ring-offset-2 ring-current shadow-lg"
                        : "opacity-70 hover:opacity-100",
                    )}
                  >
                    <div className="text-xl mb-1">{theme.emoji}</div>
                    <div className="text-child-xs font-medium">
                      {theme.name}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Book Type and Availability */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-3">
                <label className="text-child-base font-medium">Book Type</label>
                <div className="flex gap-2">
                  {(["all", "FIC", "NF"] as const).map((type) => (
                    <TISAButton
                      key={type}
                      onClick={() => setSelectedType(type)}
                      variant={selectedType === type ? "theme-blue" : "outline"}
                      size="sm"
                    >
                      {type === "all"
                        ? "All Types"
                        : type === "FIC"
                          ? "Fiction"
                          : "Non-Fiction"}
                    </TISAButton>
                  ))}
                </div>
              </div>

              <div className="space-y-3">
                <label className="text-child-base font-medium">
                  Availability
                </label>
                <div className="flex gap-2">
                  <TISAButton
                    onClick={() => setAvailableOnly(true)}
                    variant={availableOnly ? "theme-green" : "outline"}
                    size="sm"
                    icon={<BookOpen className="h-4 w-4" />}
                  >
                    Available Now
                  </TISAButton>
                  <TISAButton
                    onClick={() => setAvailableOnly(false)}
                    variant={!availableOnly ? "theme-yellow" : "outline"}
                    size="sm"
                    icon={<Clock className="h-4 w-4" />}
                  >
                    All Books
                  </TISAButton>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Search Results Summary */}
        <div className="flex items-center justify-between text-child-sm text-muted-foreground">
          <div>
            {isLoading
              ? "Loading books..."
              : `Showing ${filteredBooks.length} of ${books.length} books`}
          </div>
          {searchQuery && (
            <div>
              Searching for:{" "}
              <span className="font-semibold">"{searchQuery}"</span>
            </div>
          )}
        </div>
      </TISACard>

      {/* Loading State */}
      {isLoading && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <TISACard key={i} className="animate-pulse">
              <div className="h-48 bg-muted rounded-lg mb-4"></div>
              <div className="h-4 bg-muted rounded mb-2"></div>
              <div className="h-3 bg-muted rounded w-2/3"></div>
            </TISACard>
          ))}
        </div>
      )}

      {/* No Results */}
      {!isLoading && filteredBooks.length === 0 && (
        <TISACard className="text-center p-12">
          <div className="text-6xl mb-4">📚</div>
          <h3 className="text-child-xl font-bold mb-2">No books found</h3>
          <p className="text-muted-foreground mb-6">
            Try adjusting your search or filters to find more books
          </p>
          <TISAButton onClick={handleClearFilters} variant="theme-blue">
            Clear Filters
          </TISAButton>
        </TISACard>
      )}

      {/* Results by Theme (when not searching) */}
      {!isLoading &&
        filteredBooks.length > 0 &&
        !searchQuery &&
        selectedTheme === "all" && (
          <div className="space-y-8">
            {Object.entries(booksByTheme).map(([color, themeBooks]) => {
              if (themeBooks.length === 0) return null;

              const theme = IB_PYP_THEMES[color as ThemeColor];
              return (
                <TISACard key={color} themeColor={color as ThemeColor}>
                  <div className="space-y-6">
                    <div className="flex items-center gap-3">
                      <div className="text-3xl">{theme.emoji}</div>
                      <div>
                        <h3 className="text-child-xl font-bold">
                          {theme.name}
                        </h3>
                        <p className="text-child-sm text-muted-foreground">
                          {theme.description}
                        </p>
                      </div>
                      <div className="ml-auto text-child-sm text-muted-foreground">
                        {themeBooks.length} book
                        {themeBooks.length !== 1 ? "s" : ""}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {themeBooks.map((book) => (
                        <BookCard
                          key={book.id}
                          title={book.title}
                          author={book.author}
                          theme={book.theme_color}
                          coverUrl={book.cover_url ?? undefined}
                          available={book.copies?.some(
                            (copy) => copy.status === "available",
                          )}
                          onClick={() => navigate(`/student/books/${book.id}`)}
                        />
                      ))}
                    </div>
                  </div>
                </TISACard>
              );
            })}
          </div>
        )}

      {/* Search Results (flat grid) */}
      {!isLoading &&
        filteredBooks.length > 0 &&
        (searchQuery || selectedTheme !== "all") && (
          <TISACard>
            <div className="space-y-6">
              <div className="flex items-center gap-3">
                <Star className="h-6 w-6 text-tisa-yellow-600" />
                <h3 className="text-child-xl font-bold">Search Results</h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredBooks.map((book) => (
                  <BookCard
                    key={book.id}
                    title={book.title}
                    author={book.author}
                    theme={book.theme_color}
                    coverUrl={book.cover_url ?? undefined}
                    available={book.copies?.some(
                      (copy) => copy.status === "available",
                    )}
                    onClick={() => navigate(`/student/books/${book.id}`)}
                  />
                ))}
              </div>
            </div>
          </TISACard>
        )}
    </div>
  );
}
